/**
 * Scan-into-a-list logic shared by receiving, issuing and counting (kitchen
 * flow). Pure, so the rules are pinned by tests rather than by three screens.
 *
 * A scanned barcode names an item and a pack size (BarcodeMatch.pack_qty — the
 * carton code stands for 24 cans). On a list, the scan finds the line for that
 * item and adds one pack to it.
 *
 * The FIRST scan of a line replaces whatever was pre-filled ("everything
 * arrived", "send what was asked for") and starts the count at one pack:
 * someone who scans is counting what is physically in front of them, and
 * adding on top of a pre-fill would double it.
 */

export type ScanOutcome<L> =
  | { kind: 'added'; line: L; qty: number }
  | { kind: 'not_on_list' }

/** The qty a line holds after one more scanned pack. */
export function qtyAfterScan(current: number, packQty: number, firstScanOfLine: boolean): number {
  const pack = packQty > 0 ? packQty : 1
  const base = firstScanOfLine || !Number.isFinite(current) || current < 0 ? 0 : current
  return Math.round((base + pack) * 1e5) / 1e5
}

/** Applies one scan to a list: finds the item's line and computes its new qty. */
export function applyScanToList<L>(
  lines: L[],
  itemIdOf: (l: L) => string | undefined,
  currentQtyOf: (l: L) => number,
  scannedLines: ReadonlySet<string>,
  lineKeyOf: (l: L) => string,
  match: { item: { id: string }; pack_qty: number },
): ScanOutcome<L> {
  const line = lines.find((l) => itemIdOf(l) === match.item.id)
  if (!line) return { kind: 'not_on_list' }
  const first = !scannedLines.has(lineKeyOf(line))
  return { kind: 'added', line, qty: qtyAfterScan(currentQtyOf(line), match.pack_qty, first) }
}

/**
 * Scans that arrive faster than the screen re-renders (pilot fix, Batch 5 lane
 * tablet). A USB gun can deliver two codes before React has drawn the first
 * one, and a scan handler reading quantities from the last render then counts
 * from a stale number and loses a pack. ScanTally keeps the latest quantities
 * and the "already scanned" set itself: the screen syncs it on every render
 * (sync), each scan updates it at once (apply), and the screen stores the
 * result with a functional setState.
 */
export class ScanTally {
  private scanned = new Set<string>()
  private latest: Record<string, number> = {}

  /** The quantities as the screen last drew them (call on every render). */
  sync(qty: Record<string, number>): void {
    this.latest = qty
  }

  /** A new document or version: the next scan of each line starts afresh. */
  reset(): void {
    this.scanned = new Set()
  }

  /** One scan: finds the line, adds a pack (the first scan replaces the pre-fill). */
  apply<L>(lines: L[], itemIdOf: (l: L) => string | undefined, lineKeyOf: (l: L) => string, match: { item: { id: string }; pack_qty: number }): ScanOutcome<L> {
    const out = applyScanToList(lines, itemIdOf, (l) => this.latest[lineKeyOf(l)] ?? 0, this.scanned, lineKeyOf, match)
    if (out.kind === 'added') {
      const key = lineKeyOf(out.line)
      this.scanned.add(key)
      this.latest = { ...this.latest, [key]: out.qty }
    }
    return out
  }
}
